import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, Shield, FileText, Trash2, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Legal = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [exportLoading, setExportLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  const handleExportData = async () => {
    try {
      setExportLoading(true);
      const response = await axios.get(`${API}/auth/export-data`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `nannyou_donnees_${user?.email || 'export'}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Vos données ont été exportées');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExportLoading(false);
    }
  };
  
  const handleDeleteAccount = async () => {
    try {
      setDeleting(true);
      await axios.delete(`${API}/auth/delete-account`);
      toast.success('Votre compte a été supprimé.');
      setShowDeleteDialog(false);
      setDeleteConfirm('');
      if (logout) logout();
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };
  
  return (
    <div className="container-custom py-6 space-y-6" data-testid="legal-page">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'var(--font-secondary)' }}>
          Mentions légales & RGPD
        </h1>
        <p className="text-[var(--text-secondary)] mt-1">
          Informations légales et protection de vos données
        </p>
      </div>
      
      <Tabs defaultValue="rgpd" className="w-full">
        <TabsList className="w-full md:w-auto h-auto p-1" style={{ backgroundColor: 'var(--neutral-100)' }}>
          <TabsTrigger value="rgpd" className="gap-1 text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm">
            <Shield className="h-4 w-4" />
            RGPD
          </TabsTrigger>
          <TabsTrigger value="mentions" className="gap-1 text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm">
            <FileText className="h-4 w-4" />
            Mentions légales
          </TabsTrigger>
          <TabsTrigger value="export" className="gap-1 text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm">
            <Download className="h-4 w-4" />
            Mes données
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="rgpd" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Protection des données personnelles</CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none text-sm text-[var(--text-secondary)] space-y-4">
              <p>
                Nannyou s'engage à protéger vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD - Règlement (UE) 2016/679).
              </p>
              
              <h3 className="font-semibold text-[var(--text-primary)] text-base">Données collectées</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Données d'identité : nom, prénom, email, téléphone</li>
                <li>Données professionnelles : numéro Pajemploi, numéro d'agrément</li>
                <li>Données de contrat : conditions d'accueil, horaires, tarifs</li>
                <li>Données de pointage : heures travaillées, absences</li>
                <li>Données de facturation : historique de paiement (géré par Stripe)</li>
              </ul>
              
              <h3 className="font-semibold text-[var(--text-primary)] text-base">Finalités du traitement</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Gestion du contrat entre parent employeur et assistante maternelle</li>
                <li>Suivi du temps de travail et calcul de la rémunération</li>
                <li>Déclarations sociales (Pajemploi/Urssaf)</li>
                <li>Génération de bulletins de salaire</li>
                <li>Gestion de l'abonnement et facturation</li>
              </ul>
              
              <h3 className="font-semibold text-[var(--text-primary)] text-base">Durée de conservation</h3>
              <p>Vos données sont conservées pendant toute la durée du contrat et 5 ans après sa fin (obligations légales).</p>
              
              <h3 className="font-semibold text-[var(--text-primary)] text-base">Vos droits</h3>
              <p>Vous disposez des droits d'accès, de rectification, d'effacement, de portabilité et d'opposition.</p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="mentions" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Mentions légales</CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none text-sm text-[var(--text-secondary)] space-y-4">
              <h3 className="font-semibold text-[var(--text-primary)] text-base">Éditeur</h3>
              <p>Nannyou est édité par <span className="font-semibold">Netlyd</span>. Pour toute question, contactez <a href="mailto:contact@nannyou.fr" className="text-[var(--primary-400)] hover:underline">contact@nannyou.fr</a>.</p>
              
              <h3 className="font-semibold text-[var(--text-primary)] text-base">Hébergement</h3>
              <p>Les données sont hébergées au sein de l'Union Européenne.</p>
              
              <h3 className="font-semibold text-[var(--text-primary)] text-base">Paiement</h3>
              <p>Les paiements sont sécurisés par Stripe (PCI DSS Level 1). Nannyou ne stocke aucune donnée bancaire.</p>
              
              <h3 className="font-semibold text-[var(--text-primary)] text-base">Cookies</h3>
              <p>Nannyou utilise uniquement des cookies techniques nécessaires au fonctionnement de l'application (token d'authentification). Aucun cookie tiers ou publicitaire n'est utilisé.</p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="export" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Export de vos données</CardTitle>
              <CardDescription>
                Conformément au RGPD (Article 20), vous pouvez télécharger l'intégralité de vos données personnelles.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="border-[var(--primary-200)] bg-[var(--primary-50)]">
                <Info className="h-4 w-4" style={{ color: 'var(--primary-400)' }} />
                <AlertDescription style={{ color: 'var(--primary-700)' }}>
                  L'export inclut : vos informations de profil, contrats, heures travaillées, absences et historique de déclarations. Le fichier est au format JSON.
                </AlertDescription>
              </Alert>
              
              {user ? (
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={handleExportData}
                    disabled={exportLoading}
                    style={{ backgroundColor: 'var(--primary-400)' }}
                    data-testid="export-personal-data-btn"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {exportLoading ? 'Export en cours...' : 'Télécharger mes données'}
                  </Button>
                  <Button
                    variant="outline"
                    className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => { setShowDeleteDialog(true); setDeleteConfirm(''); }}
                    data-testid="delete-account-legal-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                    Supprimer mon compte
                  </Button>
                </div>
              ) : (
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Connectez-vous pour accéder à vos données ou supprimer votre compte.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { if (!open) { setShowDeleteDialog(false); setDeleteConfirm(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Supprimer mon compte
            </DialogTitle>
            <DialogDescription>
              Cette action est <strong>irréversible</strong>. Toutes vos données (profil, contrats,
              historique de temps, déclarations) seront définitivement supprimées.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <Label className="text-sm">
              Tapez <span className="font-mono font-bold text-red-600">SUPPRIMER</span> pour confirmer
            </Label>
            <Input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="SUPPRIMER"
              className="mt-2"
              data-testid="delete-confirm-legal-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowDeleteDialog(false); setDeleteConfirm(''); }}>Annuler</Button>
            <Button
              variant="destructive"
              disabled={deleteConfirm !== 'SUPPRIMER' || deleting}
              onClick={handleDeleteAccount}
              className="gap-2"
              data-testid="delete-confirm-legal-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {deleting ? 'Suppression…' : 'Confirmer la suppression'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Legal;
